import { IAuthenticator, AccessToken } from "./../authentication";
import { HttpClient } from "@paperbits/common/http";
import { Logger } from "@paperbits/common/logging";
import { SettingNames } from "../constants";
import { Utils } from "../utils";

const accessTokenSetting = "accessToken";
const signingSsoUrl = "/signin-sso";

export class SsoAuthenticator implements IAuthenticator {
    constructor(
        private readonly httpClient: HttpClient,
        private readonly logger: Logger
    ) { }

    private runSsoFlow(): Promise<void> {
        return new Promise<void>(async () => {
            const url = new URL(location.href);
            let tokenValue = url.searchParams.get("token");
            let returnUrl = url.searchParams.get("returnUrl") || "/";
            if (!tokenValue && url.hash.startsWith("#token=")) {
                const hashParams = new URLSearchParams(url.hash.replace(/#/g, "?"));
                tokenValue = hashParams.get("token");
                returnUrl = hashParams.get("returnUrl") || returnUrl || "/";
            }
            const tokenString = `SharedAccessSignature ${tokenValue}`;
            const token = AccessToken.parse(tokenString);

            await this.setAccessToken(token);

            // wait for redirect to happen, deliberatly not resolving the promise
            window.location.assign(Utils.sanitizeReturnUrl(returnUrl));
        });
    }

    /**
     * Check is access token can be restored from HTTP-only cookie is present
     */
    private async tryRestoreFromHttpOnlyCookie(): Promise<AccessToken> {
        const response = await this.httpClient.send<string>({ url: "/token", method: "GET" });

        if (response.statusCode !== 200) {
            return null;
        }

        try {
            const tokenValue = response.toText();
            const accessToken = AccessToken.parse(tokenValue);
            await this.setAccessToken(accessToken);

            // Return the token only if setAccessToken actually persisted it. A
            // non-2xx /sso-refresh clears local state and stores nothing, so the
            // restore must not report a rejected credential as authenticated
            // (IcM 51000001549970).
            return this.getStoredAccessToken();
        }
        catch (error) {
            return null;
        }
    }

    public async getAccessToken(): Promise<AccessToken> {
        try {
            if (location.pathname.startsWith(signingSsoUrl)) {
                await this.runSsoFlow();
            }

            const storedToken = this.getStoredAccessToken();

            if (storedToken) {
                return storedToken;
            }

            const serverToken = await this.tryRestoreFromHttpOnlyCookie();

            if (!serverToken) {
                return null;
            }

            return serverToken;
        }
        catch (error) {
            this.logger.trackError(error);
            return null;
        }
    }

    public getStoredAccessToken(): AccessToken {
        const storedToken = sessionStorage.getItem(accessTokenSetting);

        if (storedToken) {
            const accessToken = AccessToken.parse(storedToken);

            if (!accessToken.isExpired()) {
                return accessToken;
            }
            else {
                console.warn('%cAccess token expired.', 'font-weight: bold;');
                this.clearAccessToken();
            }
        }

        return null;
    }

    public async getAccessTokenAsString(): Promise<string> {
        const accessToken = await this.getAccessToken();
        return accessToken?.toString();
    }

    public async setAccessToken(accessToken: AccessToken): Promise<void> {
        if (accessToken.isExpired()) {
            console.warn(`Cannot set expired access token.`);
            return;
        }

        /* Setting up HTTP-only cookie only in published version*/
        if (!sessionStorage.getItem(SettingNames.designTimeSettings)) {
            const response = await this.httpClient.send<any>({ url: "/sso-refresh", method: "GET", headers: [{ name: "Authorization", value: accessToken.value }] });

            // The backend refresh result is the trust decision: the client cannot
            // validate a SAS signature. Only persist the token and treat the browser
            // as authenticated after an explicit success. On any failure, clear local
            // state so isAuthenticated() does not report a forged/rejected credential
            // as signed in (IcM 51000001549970).
            if (!this.isSuccessStatusCode(response.statusCode)) {
                console.warn(`SSO refresh failed with status ${response.statusCode}; access token was not stored.`);
                this.clearAccessToken();
                return;
            }
        }

        sessionStorage.setItem(accessTokenSetting, accessToken.toString());
    }

    private isSuccessStatusCode(statusCode: number): boolean {
        return statusCode >= 200 && statusCode < 300;
    }

    public clearAccessToken(): void {
        sessionStorage.removeItem(accessTokenSetting);
    }

    public async isAuthenticated(): Promise<boolean> {
        const accessToken = this.getStoredAccessToken() || await this.tryRestoreFromHttpOnlyCookie();

        return !!accessToken;
    }
}

export class RuntimeSsoAuthenticationHandler {
    constructor(
        private readonly authenticator: IAuthenticator,
        private readonly logger: Logger
    ) {
        this.start = this.start.bind(this);
        this.start().catch(error => {
            this.logger.trackError(error);
        });
    }

    public async start(): Promise<void> {
        if (!location.pathname.startsWith(signingSsoUrl)) {
            return;
        }

        // Trigger SSO authentication flow
        const result = await this.authenticator.getAccessToken();
        this.logger.trackEvent("RuntimeSsoAuthenticationHandler", { message: `SSO authentication ${result ? "succeeded" : "failed"}.` });
    }
}
